import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, UrlTree } from '@angular/router';
import { catchError, map, of, tap } from 'rxjs';
import { StorefrontApiService } from '../services/storefront-api.service';
import { StorefrontContextService } from '../services/storefront-context.service';
import { StorefrontSessionService } from '../services/storefront-session.service';

/** Query param con el que el panel ("Ver tienda") pide abrir la tienda como invitado. */
export const STOREFRONT_GUEST_VIEW_PARAM = 'vista';
export const STOREFRONT_GUEST_VIEW_VALUE = 'invitado';

/**
 * Resuelve slug → tenant; redirige URLs legacy `/store/{id}` al slug canónico.
 * Con `?vista=invitado` cierra la sesión de cliente previa del navegador y quita el parámetro.
 */
export const storefrontTenantGuard: CanActivateFn = (route, state) => {
  const api    = inject(StorefrontApiService);
  const router = inject(Router);
  const ctx    = inject(StorefrontContextService);
  const session = inject(StorefrontSessionService);

  const asGuest = (): true | UrlTree => {
    if (route.queryParamMap.get(STOREFRONT_GUEST_VIEW_PARAM) !== STOREFRONT_GUEST_VIEW_VALUE) return true;
    session.signOut(ctx.companyId());
    return router.createUrlTree([state.url.split('?')[0]], {
      queryParams: withoutGuestParam(route),
    });
  };

  const param = route.paramMap.get('storeSlug') ?? '';
  if (!param) {
    return of(router.createUrlTree(['/']));
  }

  if (/^\d+$/.test(param)) {
    const legacyId = Number(param);
    return api.resolveLegacyTenant(legacyId).pipe(
      map(tenant => {
        const suffix = state.url
          .replace(new RegExp(`^/store/${param}(?=/|$)`), '')
          .split('?')[0]
          .split('/')
          .filter(Boolean);
        return router.createUrlTree(['/store', tenant.storeSlug, ...suffix], {
          queryParams: route.queryParams,
        });
      }),
      catchError(() => of(router.createUrlTree(['/']))),
    );
  }

  if (ctx.storeSlug() === param && ctx.companyId() > 0) {
    return asGuest();
  }

  return api.resolveTenant(param).pipe(
    tap(tenant => ctx.setTenant(tenant)),
    map(() => asGuest()),
    catchError(() => of(router.createUrlTree(['/']))),
  );
};

function withoutGuestParam(route: ActivatedRouteSnapshot): Record<string, string | string[]> {
  const params: Record<string, string | string[]> = {};
  for (const key of route.queryParamMap.keys) {
    if (key !== STOREFRONT_GUEST_VIEW_PARAM) params[key] = route.queryParamMap.getAll(key);
  }
  return params;
}
